/**
 * A01a / A01b — sprite manifest schema and loader.
 *
 * An authored sprite is a directory under the adventure's sprites root:
 *
 *   public/<adv>/sprites/<entity_id>/
 *     sprite.json
 *     idle_front_0.png    idle_front_1.png    ...    idle_front_N.png
 *     idle_back_0.png     idle_back_1.png     ...                     (when directional)
 *     idle_side_0.png     idle_side_1.png     ...                     (when directional)
 *     move_front_0.png    ...                                         (optional)
 *     attack_front_0.png  ...                                         (optional)
 *     talk_front_0.png    ...                                         (NPCs, optional)
 *
 * Per-animation `frames`, `fps`, and `loop` live in `animations` in the
 * manifest. A01a engines ignore frames beyond index 0; A01b engines play
 * the full loop. Frame counts and fps are authored per entity so a
 * sluggish zombie can idle at 2 fps while a twitchy imp runs at 5 fps.
 *
 * Fallback chain on load:
 *   1. `<entity_id>/sprite.json` + directional/animated PNGs (this module)
 *   2. Flat `<entity_id>.png` (A00 loader in `SpriteManager`)
 *   3. Procedural letter-circle (pre-A00)
 */

import { getActiveAdventure } from "../adventure/ActiveAdventure.js";

export type SpriteSizeClass = "full" | "half" | "flying";
export type SpriteFacing = "N" | "S" | "E" | "W";
export type SpriteViewTag = "front" | "back" | "side";

export interface SpriteAnimation {
  /** Frame count; file names are `<anim>_<view>_<0..frames-1>.png`. */
  readonly frames: number;
  /** Playback rate in frames per second. */
  readonly fps: number;
  /** When false the animation holds on the last frame (attack, death). */
  readonly loop: boolean;
}

export interface SpriteManifest {
  /**
   * When true, the loader probes back and side PNGs on top of the front.
   * Missing back / side PNGs degrade to the front view rather than failing
   * the whole manifest.
   */
  readonly directional?: boolean;
  /** Size class hint; reserved for A01b shadow / scale tuning. */
  readonly sizeClass?: SpriteSizeClass;
  /**
   * A01b only reads this block. A01a ignores everything past
   * `animations.idle.frames[0]`, which it implicitly loads as the still
   * `<view>_0` PNG.
   */
  readonly animations?: Readonly<Record<string, SpriteAnimation>>;
  /**
   * Frame stems (`<anim>_<view>_<frameIndex>`) the renderer skips during
   * playback. The PNG stays on disk — this is a soft disable so a bad
   * generation can be hidden without losing the file for future re-rolls
   * or chroma re-runs. Authored by the SpriteEditor animation tab.
   *
   * Empty / missing → no skipping (every authored frame plays).
   */
  readonly disabledFrames?: readonly string[];
}

export interface SpriteViews {
  readonly front: HTMLImageElement;
  readonly back?: HTMLImageElement;
  readonly side?: HTMLImageElement;
}

/**
 * A01b — one animation's frames per view. Frame arrays are length ≥ 1; the
 * renderer consumes `frames[frameIndex % spec.frames]` when looping or
 * clamps to `spec.frames - 1` when `loop === false`.
 *
 * `back` / `side` are only populated when the manifest is directional AND
 * the first-frame PNG for that view loaded; missing views fall back to
 * `front` at render time.
 */
export interface LoadedAnimation {
  readonly name: string;
  readonly spec: SpriteAnimation;
  readonly front: readonly HTMLImageElement[];
  readonly back?: readonly HTMLImageElement[];
  readonly side?: readonly HTMLImageElement[];
  /**
   * Stems (`<anim>_<view>_<frameIndex>`) the renderer should skip during
   * playback. Populated from `SpriteManifest.disabledFrames`, filtered to
   * entries belonging to *this* animation so `pickFrame` doesn't have to
   * re-filter on every render. Empty when nothing is disabled — the
   * common case.
   */
  readonly disabled: ReadonlySet<string>;
}

/**
 * A04 — per-frame alignment offset in source-pixel units. Positive `x`
 * shifts the frame to the right in the final 128×192 texture; positive `y`
 * shifts it down. Authored by the authoring-server alignment UI (AS20) into
 * `<sprites>/<entityId>/<entityId>.align.json`, keyed by
 * `<anim>_<view>_<frameIndex>` (matching the PNG filename stem).
 *
 * Absent keys render at (0, 0), which is the pre-A04 behaviour, so
 * ungenerated sprites are unaffected.
 */
export interface SpriteAlignment {
  readonly x: number;
  readonly y: number;
}

export interface LoadedSprite {
  readonly manifest: SpriteManifest;
  /** Still-frame bundle (frame 0 of `idle` per view). Always present. */
  readonly views: SpriteViews;
  /**
   * Lazily-populated animation table. `idle` is preloaded when the manifest
   * declares more than one frame; other animations are loaded on first
   * use via `loadAnimation`.
   */
  readonly animations: Map<string, LoadedAnimation>;
  /**
   * A04 — frame-key → pixel offset, resolved from the `<id>.align.json`
   * sidecar. Always a Map; empty when the sidecar is absent. Frame key
   * matches the PNG filename stem: `<anim>_<view>_<frameIndex>`.
   */
  readonly align: ReadonlyMap<string, SpriteAlignment>;
}

// ── Test hooks ───────────────────────────────────────────────────────────────

type JsonLoader = (url: string) => Promise<unknown | null>;
type ImageLoader = (url: string) => Promise<HTMLImageElement | null>;

let jsonLoaderOverride: JsonLoader | null = null;
let imageLoaderOverride: ImageLoader | null = null;

export function _setSpriteSchemaLoadersForTests(
  overrides: { json?: JsonLoader | null; image?: ImageLoader | null } | null,
): void {
  if (overrides === null) {
    jsonLoaderOverride = null;
    imageLoaderOverride = null;
    return;
  }
  if (overrides.json !== undefined) jsonLoaderOverride = overrides.json;
  if (overrides.image !== undefined) imageLoaderOverride = overrides.image;
}

async function defaultJsonLoader(url: string): Promise<unknown | null> {
  try {
    const res = await fetch(url);
    if (!res.ok) return null;
    return (await res.json()) as unknown;
  } catch {
    return null;
  }
}

function defaultImageLoader(url: string): Promise<HTMLImageElement | null> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

// ── URL helpers ──────────────────────────────────────────────────────────────

function spriteDirUrl(entityId: string): string {
  const { baseUrl } = getActiveAdventure();
  return `${baseUrl}/sprites/${entityId}`;
}

// ── Schema validation (lenient) ──────────────────────────────────────────────

function validateAnimationSpec(raw: unknown): SpriteAnimation | null {
  if (typeof raw !== "object" || raw === null) return null;
  const m = raw as Record<string, unknown>;
  const frames = m["frames"];
  const fps = m["fps"];
  const loop = m["loop"];
  if (typeof frames !== "number" || !Number.isFinite(frames) || frames < 1) return null;
  if (typeof fps !== "number" || !Number.isFinite(fps) || fps <= 0) return null;
  if (typeof loop !== "boolean") return null;
  return { frames: Math.floor(frames), fps, loop };
}

function validateAnimations(raw: unknown): Readonly<Record<string, SpriteAnimation>> | undefined {
  if (typeof raw !== "object" || raw === null) return undefined;
  const out: Record<string, SpriteAnimation> = {};
  for (const [name, spec] of Object.entries(raw as Record<string, unknown>)) {
    const v = validateAnimationSpec(spec);
    if (v !== null) out[name] = v;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function validateManifest(raw: unknown): SpriteManifest | null {
  if (typeof raw !== "object" || raw === null) return null;
  const m = raw as Record<string, unknown>;
  const out: {
    directional?: boolean;
    sizeClass?: SpriteSizeClass;
    animations?: Readonly<Record<string, SpriteAnimation>>;
    disabledFrames?: readonly string[];
  } = {};
  if (typeof m["directional"] === "boolean") {
    out.directional = m["directional"];
  }
  if (m["sizeClass"] === "full" || m["sizeClass"] === "half" || m["sizeClass"] === "flying") {
    out.sizeClass = m["sizeClass"];
  }
  const anims = validateAnimations(m["animations"]);
  if (anims !== undefined) out.animations = anims;
  const disabled = m["disabledFrames"];
  if (Array.isArray(disabled)) {
    // Filter to well-formed stems; malformed entries are dropped
    // silently rather than failing the whole manifest.
    const cleaned = disabled.filter(
      (s): s is string => typeof s === "string" && /^[a-z]+_(front|back|side)_\d+$/.test(s),
    );
    if (cleaned.length > 0) out.disabledFrames = cleaned;
  }
  return out;
}

// ── Public loader ────────────────────────────────────────────────────────────

/**
 * Probe `<sprites>/<entity_id>/sprite.json` and load the `idle` animation.
 * The returned bundle always carries first-frame `views` for backwards
 * compatibility; when the manifest declares an `idle` animation with more
 * than one frame, the full frame list per view is preloaded into
 * `animations.idle`. Other animations (move/attack/talk/…) are loaded
 * lazily via `loadAnimation` on first state transition.
 *
 * Returns null when the manifest is absent or malformed — callers fall
 * back to the A00 flat-PNG probe.
 */
export async function loadAuthoredSprite(entityId: string): Promise<LoadedSprite | null> {
  const jsonLoader = jsonLoaderOverride ?? defaultJsonLoader;
  const imageLoader = imageLoaderOverride ?? defaultImageLoader;

  const dir = spriteDirUrl(entityId);
  const raw = await jsonLoader(`${dir}/sprite.json`);
  if (raw === null) return null;
  const manifest = validateManifest(raw);
  if (manifest === null) return null;

  const front = await imageLoader(`${dir}/idle_front_0.png`);
  if (!front) return null; // every authored sprite must at minimum ship idle_front_0.

  const views: { front: HTMLImageElement; back?: HTMLImageElement; side?: HTMLImageElement } = {
    front,
  };

  if (manifest.directional === true) {
    const [back, side] = await Promise.all([
      imageLoader(`${dir}/idle_back_0.png`),
      imageLoader(`${dir}/idle_side_0.png`),
    ]);
    if (back) views.back = back;
    if (side) views.side = side;
  }

  const animations = new Map<string, LoadedAnimation>();

  // A04 — best-effort alignment sidecar. Absent / malformed → empty map.
  const align = await loadAlignSidecar(entityId, jsonLoader);

  // Eagerly preload the idle animation so entities start their loop on
  // their first rendered frame. Missing extra frames degrade to the
  // first-frame views that are already loaded.
  const idleSpec = manifest.animations?.["idle"];
  if (idleSpec && idleSpec.frames > 1) {
    const idle = await loadAnimationFrames(
      entityId,
      "idle",
      idleSpec,
      manifest.directional === true,
      views,
      manifest.disabledFrames,
    );
    if (idle) animations.set("idle", idle);
  } else if (idleSpec) {
    // 1-frame idle: synthesise a LoadedAnimation from the already-loaded views.
    animations.set(
      "idle",
      buildAnimationFromViews("idle", idleSpec, views, manifest.disabledFrames),
    );
  }

  return { manifest, views, animations, align };
}

/**
 * Filter a manifest's `disabledFrames` list to just the stems that
 * belong to the named animation. Returned as a Set so `pickFrame`
 * can do O(1) lookups. Empty set when nothing is disabled for this
 * animation (the common case).
 */
function disabledForAnim(animName: string, all: readonly string[] | undefined): Set<string> {
  if (!all || all.length === 0) return new Set<string>();
  const prefix = `${animName}_`;
  const out = new Set<string>();
  for (const stem of all) {
    if (stem.startsWith(prefix)) out.add(stem);
  }
  return out;
}

/**
 * A04 — probe `<sprites>/<entity_id>/<entity_id>.align.json`. The file is
 * a flat `{ "<anim>_<view>_<frame>": { "x": number, "y": number } }`
 * object; non-numeric or non-object entries are silently dropped so a
 * half-finished sidecar can't brick the render. Returns an empty map when
 * the file is absent.
 */
async function loadAlignSidecar(
  entityId: string,
  jsonLoader: JsonLoader,
): Promise<ReadonlyMap<string, SpriteAlignment>> {
  const raw = await jsonLoader(`${spriteDirUrl(entityId)}/${entityId}.align.json`);
  const out = new Map<string, SpriteAlignment>();
  if (typeof raw !== "object" || raw === null) return out;
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value !== "object" || value === null) continue;
    const v = value as Record<string, unknown>;
    const x = v["x"];
    const y = v["y"];
    if (typeof x !== "number" || !Number.isFinite(x)) continue;
    if (typeof y !== "number" || !Number.isFinite(y)) continue;
    out.set(key, { x, y });
  }
  return out;
}

/** A04 — public frame-key builder so the authoring UI and the engine agree. */
export function alignKey(anim: string, view: SpriteViewTag, frameIndex: number): string {
  return `${anim}_${view}_${String(frameIndex)}`;
}

/**
 * Lazily load an animation's full frame list per view. Called by the
 * renderer when an entity transitions into a state whose animation isn't
 * preloaded. Returns null when the front-0 frame is absent (the animation
 * simply isn't authored and the caller should fall back to `idle`).
 *
 * The caller is expected to pass `manifest.animations[name]` as the spec
 * and `manifest.directional === true` as `directional`. `frame0Hints`
 * lets the caller skip a network request when the idle-view PNG for a
 * given view has already been loaded — currently unused for non-idle
 * animations but reserved so future hit-flash / death animations can
 * reuse any already-decoded frames.
 */
export async function loadAnimation(
  entityId: string,
  animName: string,
  spec: SpriteAnimation,
  directional: boolean,
  disabledFrames?: readonly string[],
): Promise<LoadedAnimation | null> {
  return loadAnimationFrames(entityId, animName, spec, directional, null, disabledFrames);
}

function buildAnimationFromViews(
  name: string,
  spec: SpriteAnimation,
  views: { front: HTMLImageElement; back?: HTMLImageElement; side?: HTMLImageElement },
  disabledFrames?: readonly string[],
): LoadedAnimation {
  const out: {
    name: string;
    spec: SpriteAnimation;
    front: HTMLImageElement[];
    back?: HTMLImageElement[];
    side?: HTMLImageElement[];
    disabled: Set<string>;
  } = { name, spec, front: [views.front], disabled: disabledForAnim(name, disabledFrames) };
  if (views.back) out.back = [views.back];
  if (views.side) out.side = [views.side];
  return out;
}

async function loadAnimationFrames(
  entityId: string,
  animName: string,
  spec: SpriteAnimation,
  directional: boolean,
  frame0Hints: { front: HTMLImageElement; back?: HTMLImageElement; side?: HTMLImageElement } | null,
  disabledFrames?: readonly string[],
): Promise<LoadedAnimation | null> {
  const imageLoader = imageLoaderOverride ?? defaultImageLoader;
  const dir = spriteDirUrl(entityId);

  const loadView = async (
    view: SpriteViewTag,
    hint: HTMLImageElement | undefined,
  ): Promise<HTMLImageElement[] | null> => {
    const frames: HTMLImageElement[] = [];
    for (let i = 0; i < spec.frames; i++) {
      const existing = i === 0 ? hint : undefined;
      const img = existing ?? (await imageLoader(`${dir}/${animName}_${view}_${i}.png`));
      if (!img) {
        // Partial frame lists degrade gracefully — keep what we loaded so
        // the renderer can still play a shortened cycle. An empty front
        // list is a hard miss.
        break;
      }
      frames.push(img);
    }
    return frames.length > 0 ? frames : null;
  };

  const front = await loadView("front", frame0Hints?.front);
  if (!front) return null;

  const out: {
    name: string;
    spec: SpriteAnimation;
    front: HTMLImageElement[];
    back?: HTMLImageElement[];
    side?: HTMLImageElement[];
    disabled: Set<string>;
  } = { name: animName, spec, front, disabled: disabledForAnim(animName, disabledFrames) };

  if (directional) {
    const [back, side] = await Promise.all([
      loadView("back", frame0Hints?.back),
      loadView("side", frame0Hints?.side),
    ]);
    if (back) out.back = back;
    if (side) out.side = side;
  }

  return out;
}

// ── View selection ──────────────────────────────────────────────────────────

/** Cardinal direction from one point to another, tie-breaking toward the z axis. */
export function cardinalFromTo(
  from: { x: number; z: number },
  to: { x: number; z: number },
): SpriteFacing {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  if (Math.abs(dx) > Math.abs(dz)) {
    return dx >= 0 ? "E" : "W";
  }
  return dz >= 0 ? "S" : "N";
}

const CLOCKWISE: readonly SpriteFacing[] = ["N", "E", "S", "W"];

function turns(from: SpriteFacing, to: SpriteFacing): 0 | 1 | 2 | 3 {
  const a = CLOCKWISE.indexOf(from);
  const b = CLOCKWISE.indexOf(to);
  return ((((b - a) % 4) + 4) % 4) as 0 | 1 | 2 | 3;
}

export interface ViewSelection {
  /** Which PNG to draw. */
  readonly tag: SpriteViewTag;
  /** True when the side view should be horizontally mirrored. */
  readonly mirror: boolean;
}

/**
 * Resolve which view tag to draw given the entity's facing and the camera's
 * position relative to the entity. Mirrors the side view when the camera
 * sits off the entity's right rather than its left.
 *
 * front = camera is where the entity is looking.
 * back  = camera is directly behind.
 * side  = camera is off to one side; `mirror` flips the PNG horizontally
 *         so a single `*_side_*` asset covers both left and right flanks.
 *
 * Mirror convention: every shipped `*_side_*` asset faces SCREEN-LEFT
 * (the generation pipeline consistently produces left-facing profiles
 * regardless of prompt wording, and the hand-made sets followed suit).
 * When the camera is off the entity's LEFT flank, the nose should point
 * screen-left → unmirrored art is already correct. Off the RIGHT flank
 * the nose must point screen-right → mirror. Getting this backwards
 * renders every flanked entity walking nose-behind-it.
 */
export function selectView(facing: SpriteFacing, cameraRelativeTo: SpriteFacing): ViewSelection {
  const t = turns(facing, cameraRelativeTo);
  // 0: facing toward camera     → front
  // 1: camera is to the right   → side (mirrored: nose points screen-right)
  // 2: camera behind            → back
  // 3: camera is to the left    → side (unmirrored: art already faces screen-left)
  if (t === 0) return { tag: "front", mirror: false };
  if (t === 2) return { tag: "back", mirror: false };
  return { tag: "side", mirror: t === 1 };
}

// ── Frame selection ─────────────────────────────────────────────────────────

/**
 * A01b — pick the specific HTMLImageElement for a given animation + view +
 * frame index. Falls back to `front` when the requested view isn't
 * authored. Returns null when even `front` is empty (should never happen
 * on a well-formed LoadedAnimation).
 *
 * The `frameIndex` is wrapped modulo `spec.frames` when `loop === true`
 * and clamped to the last frame otherwise. Extra frames beyond what was
 * actually loaded (partial manifests) are also clamped — the renderer
 * still sees a valid image.
 */
export function pickFrame(
  anim: LoadedAnimation,
  view: SpriteViewTag,
  frameIndex: number,
): HTMLImageElement | null {
  const bank = view === "back" ? anim.back : view === "side" ? anim.side : anim.front;
  const frames = bank && bank.length > 0 ? bank : anim.front;
  if (frames.length === 0) return null;
  let wrapped = anim.spec.loop
    ? ((frameIndex % anim.spec.frames) + anim.spec.frames) % anim.spec.frames
    : Math.min(frameIndex, anim.spec.frames - 1);
  // Soft-disabled frames (authored via the SpriteEditor animation tab)
  // get skipped here so the renderer's frameIndex advancement upstream
  // doesn't have to know about per-view disable state. Walk forward at
  // most `spec.frames` times so an entirely-disabled animation degrades
  // to displaying the originally-requested frame rather than spinning
  // forever.
  if (anim.disabled.size > 0) {
    for (let attempts = 0; attempts < anim.spec.frames; attempts++) {
      if (!anim.disabled.has(`${anim.name}_${view}_${String(wrapped)}`)) break;
      wrapped = anim.spec.loop
        ? (wrapped + 1) % anim.spec.frames
        : Math.min(wrapped + 1, anim.spec.frames - 1);
    }
  }
  const clamped = Math.min(wrapped, frames.length - 1);
  return frames[clamped] ?? null;
}

// ── State → animation mapping ───────────────────────────────────────────────

/**
 * A01b — canonical animation names. Every authored manifest should pin
 * `idle`; the others are opt-in per entity and fall back to `idle` when
 * absent.
 */
export type SpriteAnimationName = "idle" | "move" | "attack" | "talk";

/**
 * Map an `enemyMachine` state value to its animation name. The engine
 * swaps animations on state-change, so this is the single source of
 * truth for which enemy states play which PNGs.
 *
 * - `idle`, `alert`, `alerted` → `idle` (calm / watchful — same loop)
 * - `patrolling`               → `move`
 * - `combat`, `attacking`      → `attack`
 * - `dead`                     → `idle` (freeze frame; death-fade handled separately)
 */
export function animationForEnemyState(state: string): SpriteAnimationName {
  if (state === "patrolling") return "move";
  if (state === "combat" || state === "attacking") return "attack";
  return "idle";
}
